<template>
  <div class="n-tour-step-host" :class="{ 'is-inline': inline, 'is-hidden': !inline || !show }">
    <div ref="content" :id="cardId" class="n-tour-step-content-root" role="status" aria-live="polite">
      <div class="n-tour-step-card">
        <div class="n-tour-step-header">
          <p v-if="stepLabel" class="n-tour-step-step-label">{{ stepLabel }}</p>
          <div class="n-tour-step-controls">
            <button
              type="button"
              class="n-tour-step-control"
              :aria-label="dismissLabel"
              :title="dismissLabel"
              @click="$emit('dismiss')"
            >
              <n-icon icon="x" size="s" />
            </button>
          </div>
        </div>
        <p v-if="title" class="n-tour-step-title">{{ title }}</p>
        <p class="n-tour-step-description">{{ description }}</p>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Ref, Vue, Watch } from 'vue-property-decorator'
import tippy from 'tippy.js'
// `import type` is required for Placement: it is referenced in a decorated @Prop signature, and
// isolatedModules + emitDecoratorMetadata would otherwise try to emit a runtime reference to it.
import type { Instance, Placement } from 'tippy.js'

// Per-instance, generated once at construction rather than per sync: a re-sync that minted a new id
// would leave the old one stranded in the anchor's aria-describedby with nothing to resolve to.
let nextTourStepId = 0

/**
 * Guidance for walking a first-time user through a flow.
 *
 * Two ways to place it, and which one is right depends on whether there is room beside the thing it
 * describes. By default it is a floating card anchored to that control with tippy - the same
 * anchoring the rest of the app's popovers use, so it flips and shifts itself away from viewport
 * edges rather than needing per-step positioning. Pass `inline` where the surface fills its own
 * container (a full-width modal), and it takes part in the host layout instead: a floating card
 * there has nowhere to go that is not on top of the content it is asking the user to read.
 *
 * One control: x dismisses the tour for good. The host persists that choice and hides the tour.
 * There is no Next: a step advances by doing the thing it describes.
 *
 * Purely presentational - the host owns what dismissing means.
 *
 * Pass `scrollContainer` when the anchor lives inside a scrolling region (a modal gallery): the
 * card is appended there with absolute positioning so it scrolls and clips with the content instead
 * of floating fixed over the viewport.
 *
 * `title` is optional: where the host already has a heading of its own, repeating it here just
 * stacks two headings on top of each other.
 */
@Component
export default class NTourStep extends Vue {
  @Prop({ required: true }) description!: string
  /** What to anchor to: an element, or a selector resolved against the document. */
  @Prop({ default: null }) target!: HTMLElement | string | null
  @Prop({ default: '' }) title!: string
  @Prop({ default: false, type: Boolean }) show!: boolean
  @Prop({ default: '' }) stepLabel!: string
  @Prop({ default: 'bottom-start' }) placement!: Placement
  /** Take part in the host layout instead of floating over it. Ignores `target` and `placement`. */
  @Prop({ default: false, type: Boolean }) inline!: boolean
  /**
   * Scroll container to append the expanded popper into. Keeps the callout inside a modal's
   * scrolling region so it moves with the anchor instead of overflowing the modal edge.
   */
  @Prop({ default: null }) scrollContainer!: HTMLElement | string | null

  @Ref('content') contentRef!: HTMLElement

  private tippyInstance: Instance | null = null
  /**
   * Invalidates a scheduled popper creation. Creation is deferred by a tick, and several things
   * routinely ask for a resync inside one - mounting, then the host filling in its target ref -
   * so without this both callbacks built a popper. The second took the content node with it and
   * the first was left as an empty box, invisible except for its arrow: a stray caret floating
   * beside the real card, with no element under it to explain where it came from.
   */
  private syncToken = 0

  private readonly cardId = `n-tour-step-${++nextTourStepId}`

  /** The anchor this step currently describes, so teardown detaches from the right node. */
  private describedElement: HTMLElement | null = null

  get dismissLabel(): string {
    return this.$t('general.dismissTips') as string
  }

  mounted() {
    this.syncTippy()
  }

  beforeDestroy() {
    this.destroyTippy()
  }

  @Watch('show')
  @Watch('target')
  @Watch('placement')
  @Watch('inline')
  @Watch('scrollContainer')
  onAnchorChange() {
    this.syncTippy()
  }

  /**
   * The card changes height when the copy changes, and popper only recomputes on its own for
   * scroll/resize - so nudge it once Vue has patched the new content in, otherwise the arrow keeps
   * pointing at where the old card used to end.
   */
  @Watch('title')
  @Watch('description')
  @Watch('stepLabel')
  onContentChange() {
    this.$nextTick(() => {
      void this.tippyInstance?.popperInstance?.update()
    })
  }

  private resolveTarget(): HTMLElement | null {
    return this.resolveElement(this.target)
  }

  private resolveScrollContainer(): HTMLElement | null {
    return this.resolveElement(this.scrollContainer)
  }

  private resolveElement(element: HTMLElement | string | null): HTMLElement | null {
    if (!element) {
      return null
    }
    if (typeof element === 'string') {
      return document.querySelector<HTMLElement>(element)
    }
    return element
  }

  private syncTippy() {
    this.destroyTippy()
    const token = ++this.syncToken
    if (!this.show || this.inline) {
      return
    }
    // Wait a tick: hosts commonly flip `show` in the same patch that renders the target, so the
    // element is not in the document yet at the moment the watcher fires.
    this.$nextTick(() => {
      const target = this.resolveTarget()
      // No anchor means no guidance, rather than a card floating in a corner with nothing to point
      // at. The flow still works - the guidance is an explainer, never a gate.
      if (token !== this.syncToken || !this.show || !target || !this.contentRef) {
        return
      }
      const scrollContainer = this.resolveScrollContainer()
      const contained = scrollContainer !== null
      this.tippyInstance = tippy(target, {
        theme: 'notch-tour',
        content: this.contentRef,
        placement: this.placement,
        appendTo: () => (contained && scrollContainer ? scrollContainer : document.body),
        interactive: true,
        arrow: true,
        offset: [0, 8],
        // A phone is narrower than the card's natural width, and tippy applies this verbatim, so
        // clamp rather than letting preventOverflow squeeze it into an unreadable column.
        maxWidth: 'min(340px, calc(100vw - 2rem))',
        trigger: 'manual',
        hideOnClick: false,
        showOnCreate: true,
        popperOptions: {
          strategy: contained ? 'absolute' : 'fixed',
          modifiers: [
            {
              name: 'preventOverflow',
              options: { padding: 12, boundary: contained && scrollContainer ? scrollContainer : 'clippingParents' }
            },
            // popper only tries the directly opposite placement on its own. Beside-the-anchor is
            // the intended shape, so the other side comes first; the perpendicular placements are
            // there for anchors that span the full width (the stacked layout's collapsed sidebar),
            // where neither side has room and the card would otherwise sit half off-screen.
            {
              name: 'flip',
              options: { fallbackPlacements: ['right-start', 'left-start', 'bottom-start', 'top-start'] }
            }
          ]
        }
      })
      // The popper is appended to the body, far from the anchor in DOM order, so without this a
      // screen reader reaching the ringed control gets a button and no explanation of what the tour
      // is asking for.
      this.attachDescribedBy(target)
    })
  }

  /**
   * `aria-describedby` is a token set, not a value to own: another component may add or remove its
   * own token while this step is up, so we add ours if absent and later remove only ours, rather
   * than snapshotting the attribute and restoring it wholesale.
   */
  private attachDescribedBy(target: HTMLElement) {
    if (this.describedElement === target) {
      return
    }
    this.detachDescribedBy()
    const tokens = this.describedByTokens(target)
    if (!tokens.includes(this.cardId)) {
      tokens.push(this.cardId)
    }
    target.setAttribute('aria-describedby', tokens.join(' '))
    this.describedElement = target
  }

  private detachDescribedBy() {
    const target = this.describedElement
    this.describedElement = null
    if (!target) {
      return
    }
    const tokens = this.describedByTokens(target).filter(token => token !== this.cardId)
    if (tokens.length) {
      target.setAttribute('aria-describedby', tokens.join(' '))
    } else {
      target.removeAttribute('aria-describedby')
    }
  }

  private describedByTokens(element: HTMLElement): string[] {
    return (element.getAttribute('aria-describedby') ?? '').split(/\s+/).filter(Boolean)
  }

  private destroyTippy() {
    // Also cancels a creation that is still pending from an earlier call.
    this.syncToken++
    // Before the early return: the anchor can change without a popper ever having been built.
    this.detachDescribedBy()
    if (!this.tippyInstance) {
      return
    }
    // tippy moves the content node into its popper; put it back under this component first so Vue
    // still owns a node it can patch and unmount.
    if (this.contentRef && this.$el) {
      this.$el.appendChild(this.contentRef)
    }
    this.tippyInstance.destroy()
    this.tippyInstance = null
  }
}
</script>

<style scoped lang="scss">
@use '@/assets/styles' as *;

// Anchored mode moves the card into a tippy popper, so the host is left holding nothing and must
// not take up space; inline mode keeps the card here and stretches it across the slot the host gave
// it. `is-hidden` also covers `show: false`, where there is nothing to render either way.
.n-tour-step-host {
  display: none;

  &.is-inline {
    display: block;
    width: 100%;
  }

  &.is-hidden {
    display: none;
  }
}

// In flow the card draws its own surface; in anchored mode the tippy theme below draws the same one
// around it. Deliberately not notch-style-floating: that is a 2px border plus a hard 2px/3px offset
// shadow, and tippy's pointer is a solid triangle drawn at the box's edge, so the pointer, the
// border line and the shadow's offset edge stacked into what read as a second caret beside the
// first. A soft shadow with a thin sand border lets the pointer merge into the card as one shape.
//
// Warm accent tint (same family as NBanner info) so the callout stands out on white/sand surfaces
// without the harsh dark coachmark look, and pairs with the .n-tour-highlight ring on the anchor.
.is-inline .n-tour-step-card {
  box-sizing: border-box;
  width: 100%;
  background-color: $notch-color-semantic-accent-100;
  border: 1px solid $notch-color-neutral-sand-200;
  border-radius: $notch-radius-2x;
  box-shadow: 0 $notch-space-2x $notch-space-6x rgba($notch-color-neutral-grey-900, 0.12);
}

.n-tour-step-card {
  box-sizing: border-box;
  padding: $notch-space-5x;
  text-align: left;
}

.n-tour-step-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: $notch-space-3x;
  margin-bottom: $notch-space-2x;
}

// Grey, not accent. accent-800 (#B39500) on accent-100 (#FFFCEB) is ~2.8:1;
// this label is XS so WCAG AA needs 4.5:1. grey-700 is ~5.1:1 on the same tint.
// Do not restore accent-800 here; the card background already carries the accent hue.
.n-tour-step-step-label {
  @include notch-text-label-medium-XS;
  color: $notch-color-neutral-grey-700;
}

.n-tour-step-controls {
  display: flex;
  flex: none;
  align-items: center;
  gap: $notch-space-1x;
  // Pulls the icon row back towards the card's padding so it optically aligns with the step label.
  margin: (-$notch-space-1x) (-$notch-space-1x) 0 0;
}

// Legible but quiet against the light card: the exit is not the point of the step.
.n-tour-step-control {
  display: flex;
  flex: none;
  align-items: center;
  justify-content: center;
  padding: $notch-space-1x;
  border: none;
  border-radius: $notch-radius-1x;
  background: none;
  color: $notch-color-neutral-grey-600;
  cursor: pointer;

  &:hover {
    background-color: $notch-color-neutral-sand-200;
    color: $notch-color-neutral-grey-900;
  }
}

.n-tour-step-title {
  @include notch-text-label-strong-M;
  color: $notch-color-neutral-grey-900;
  margin-bottom: $notch-space-2x;
}

.n-tour-step-description {
  @include notch-text-label-light-XS;
  color: $notch-color-neutral-grey-700;
}
</style>

<style lang="scss">
@use '@/assets/styles' as *;

// tippy owns the box and the arrow, so neither can be reached from the scoped block above.
//
// Warm accent tint matching the inline card above and NBanner info - visible on light surfaces
// without the harsh dark coachmark, and paired with the .n-tour-highlight ring on the anchor.
.tippy-box[data-theme~='notch-tour'] {
  background-color: $notch-color-semantic-accent-100;
  color: $notch-color-neutral-grey-900;
  border-radius: $notch-radius-2x;
  // See the inline card above for why this is a soft shadow rather than notch-style-floating.
  border: 1px solid $notch-color-neutral-sand-200;
  box-shadow: 0 $notch-space-2x $notch-space-6x rgba($notch-color-neutral-grey-900, 0.12);

  > .tippy-content {
    padding: 0;
  }

  // The pointer is the card's own background continuing past its edge, so it reads as one shape
  // rather than as a separate mark sitting next to it. tippy fills the arrow from `currentColor`,
  // and per-placement `::before` overrides only ever added a second, differently coloured edge.
  > .tippy-arrow {
    color: $notch-color-semantic-accent-100;
  }
}
</style>
